import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../../firebase";
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";

function AddAnnouncementsPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [isManager, setIsManager] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [publishedAnnouncements, setPublishedAnnouncements] = useState([]);

  // Date picker state - default to today's date
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  );

  // Character limit and validation
  const MAX_TITLE_LENGTH = 100;
  const MAX_CONTENT_LENGTH = 1000;
  const [titleChars, setTitleChars] = useState(0);
  const [contentChars, setContentChars] = useState(0);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          setTeam(teamData);
          const currentUser = auth.currentUser;
          if (currentUser && currentUser.uid === teamData.coachId) {
            setIsManager(true);
          }
        } else {
          setMessage({ text: "Team not found", type: "error" });
        }
      } catch (err) {
        console.error("Error fetching team:", err);
        setMessage({ text: "Error loading team information", type: "error" });
      }
      setLoading(false);
    };

    fetchTeam();
  }, [teamId]);

  const handleTitleChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_TITLE_LENGTH) {
      setTitle(value);
      setTitleChars(value.length);
    }
  };

  const handleContentChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_CONTENT_LENGTH) {
      setContent(value);
      setContentChars(value.length);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      setMessage({ text: "Title and content cannot be empty", type: "error" });
      return;
    }

    setIsSubmitting(true);
    try {
      // Parse selected date
      const [year, month, day] = selectedDate.split('-').map(num => parseInt(num));
      const announcementDateObj = new Date(year, month - 1, day);

      // Create announcement data object
      const announcementData = {
        teamId,
        title: title.trim(),
        content: content.trim(),
        announcementDate: announcementDateObj,
        createdAt: new Date(), // Use current time for display
        createdBy: auth.currentUser ? auth.currentUser.uid : null,
        teamName: team ? team.name : "Unknown Team",
      };

      // Add to Firestore
      const docRef = await addDoc(collection(db, "announcements"), {
        ...announcementData,
        createdAt: serverTimestamp(), // Use serverTimestamp for the database
      });

      // Add to local state with the document ID
      setPublishedAnnouncements([
        ...publishedAnnouncements, 
        { id: docRef.id, ...announcementData }
      ]);

      // Reset form
      setTitle("");
      setContent("");
      setTitleChars(0);
      setContentChars(0);
      setSelectedDate(
        `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      );
      
      setMessage({ text: "Announcement published successfully!", type: "success" });
      
      // Automatically clear success message after 5 seconds
      setTimeout(() => {
        setMessage({ text: "", type: "" });
      }, 5000);
      
    } catch (err) {
      console.error("Error adding announcement:", err);
      setMessage({ text: "Failed to publish announcement. Please try again.", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/team/${teamId}/announcements`);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={loaderContainerStyle}>
        <div style={loaderStyle}></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isManager) {
    return (
      <div style={unauthorizedStyle}>
        <h2>Unauthorized Access</h2>
        <p>You don't have permission to create announcements for this team.</p>
        <button
          onClick={() => navigate(`/team/${teamId}`)}
          style={buttonSecondaryStyle}
        >
          Return to Team Page
        </button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h2 style={headerStyle}>New Team Announcement</h2>
      <p style={subheaderStyle}>
        {team && `Creating announcement for: ${team.name}`}
      </p>

      <form onSubmit={handleSubmit}>
        <div style={inputGroupStyle}>
          <div style={labelContainerStyle}>
            <label htmlFor="title" style={labelStyle}>
              Announcement Title
            </label>
            <span style={characterCountStyle}>
              {titleChars}/{MAX_TITLE_LENGTH}
            </span>
          </div>
          <input
            type="text"
            id="title"
            value={title}
            onChange={handleTitleChange}
            style={inputStyle}
            placeholder="Enter announcement title"
            required
          />
        </div>

        <div style={inputGroupStyle}>
          <label htmlFor="date" style={labelStyle}>
            Date
          </label>
          <input
            type="date"
            id="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={inputStyle}
            min="2020-01-01"
            max="2030-12-31"
          />
        </div>

        <div style={inputGroupStyle}>
          <div style={labelContainerStyle}>
            <label htmlFor="content" style={labelStyle}>
              Content
            </label>
            <span style={characterCountStyle}>
              {contentChars}/{MAX_CONTENT_LENGTH}
            </span>
          </div>
          <textarea
            id="content"
            value={content}
            onChange={handleContentChange}
            style={textareaStyle}
            placeholder="Enter announcement details..."
            required
          ></textarea>
        </div>

        <div style={buttonContainerStyle}>
          <button
            type="button"
            onClick={handleCancel}
            style={buttonCancelStyle}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={isSubmitting ? buttonDisabledStyle : buttonPrimaryStyle}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Publishing..." : "Publish Announcement"}
          </button>
        </div>
      </form>

      {message.text && (
        <div 
          style={message.type === "success" ? successMessageStyle : errorMessageStyle}
          onClick={() => setMessage({ text: "", type: "" })}
        >
          {message.text}
        </div>
      )}
      
      {/* Published Announcements Section */}
      {publishedAnnouncements.length > 0 && (
        <div style={publishedAnnouncementsContainerStyle}>
          <h3 style={publishedAnnouncementsHeaderStyle}>Recently Published Announcements</h3>
          <div style={announcementsListStyle}>
            {publishedAnnouncements.map((announcement) => (
              <div key={announcement.id} style={announcementCardStyle}>
                <div style={announcementHeaderStyle}>
                  <h4 style={announcementTitleStyle}>{announcement.title}</h4>
                  <span style={announcementDateStyle}>
                    {formatDate(announcement.announcementDate)}
                  </span>
                </div>
                <p style={announcementContentStyle}>
                  {announcement.content.length > 150 
                    ? `${announcement.content.substring(0, 150)}...` 
                    : announcement.content}
                </p>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate(`/team/${teamId}/dashboard/announcements`)}
            style={viewAllButtonStyle}
          >
            View All Announcements
          </button>
        </div>
      )}
    </div>
  );
}

// Original styles maintained
const containerStyle = {
  padding: "2rem",
  maxWidth: "700px",
  margin: "2rem auto",
  fontFamily: "Inter, system-ui, sans-serif",
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
};

const headerStyle = {
  textAlign: "center",
  color: "#1e293b",
  fontSize: "1.875rem",
  fontWeight: "700",
  marginBottom: "0.5rem",
};

const subheaderStyle = {
  textAlign: "center",
  color: "#64748b",
  fontSize: "1rem",
  marginBottom: "2rem",
  paddingBottom: "1rem",
  borderBottom: "2px solid #e2e8f0",
};

const inputGroupStyle = {
  marginBottom: "1.5rem",
};

const labelContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "0.5rem",
};

const labelStyle = {
  fontWeight: "600",
  color: "#334155",
  fontSize: "0.875rem",
};

const characterCountStyle = {
  fontSize: "0.75rem",
  color: "#64748b",
};

const inputStyle = {
  width: "100%",
  padding: "0.75rem",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  backgroundColor: "#f8fafc",
  fontSize: "1rem",
  transition: "all 0.2s",
  outline: "none",
  boxSizing: "border-box",
  ":focus": {
    borderColor: "#6366f1",
    boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.2)",
  },
};

const textareaStyle = {
  ...inputStyle,
  height: "180px",
  resize: "vertical",
  lineHeight: "1.5",
};

const buttonContainerStyle = {
  display: "flex",
  gap: "1rem",
  marginTop: "2rem",
};

const buttonPrimaryStyle = {
  flex: "1",
  padding: "0.875rem",
  color: "#ffffff",
  backgroundColor: "#4f46e5",
  border: "none",
  borderRadius: "8px",
  fontSize: "1rem",
  fontWeight: "600",
  cursor: "pointer",
  transition: "all 0.2s",
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  ":hover": {
    backgroundColor: "#4338ca",
    transform: "translateY(-1px)",
  },
};

const buttonCancelStyle = {
  flex: "1",
  padding: "0.875rem",
  backgroundColor: "#ffffff",
  color: "#4b5563",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  fontSize: "1rem",
  fontWeight: "600",
  cursor: "pointer",
  transition: "all 0.2s",
  ":hover": {
    backgroundColor: "#f9fafb",
  },
};

const buttonSecondaryStyle = {
  padding: "0.75rem 1.5rem",
  backgroundColor: "#f1f5f9",
  color: "#334155",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "1rem",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.2s",
  textDecoration: "none",
  display: "inline-block",
  ":hover": {
    backgroundColor: "#e2e8f0",
  },
};

const buttonDisabledStyle = {
  ...buttonPrimaryStyle,
  backgroundColor: "#94a3b8",
  cursor: "not-allowed",
  opacity: 0.7,
};

const successMessageStyle = {
  marginTop: "1.5rem",
  padding: "1rem",
  backgroundColor: "#dcfce7",
  color: "#166534",
  borderRadius: "8px",
  textAlign: "center",
  fontSize: "0.875rem",
  fontWeight: "500",
  cursor: "pointer",
  animation: "fadeIn 0.3s ease-in-out",
};

const errorMessageStyle = {
  marginTop: "1.5rem",
  padding: "1rem",
  backgroundColor: "#fee2e2",
  color: "#991b1b",
  borderRadius: "8px",
  textAlign: "center",
  fontSize: "0.875rem",
  fontWeight: "500",
  cursor: "pointer",
  animation: "fadeIn 0.3s ease-in-out",
};

const loaderContainerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "300px",
  gap: "1rem",
  color: "#64748b",
};

const loaderStyle = {
  border: "4px solid #f3f3f3",
  borderRadius: "50%",
  borderTop: "4px solid #4f46e5",
  width: "40px",
  height: "40px",
  animation: "spin 1s linear infinite",
};

const unauthorizedStyle = {
  textAlign: "center",
  padding: "3rem",
  maxWidth: "600px",
  margin: "3rem auto",
  backgroundColor: "#f8fafc",
  borderRadius: "12px",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
};

// New styles for published announcements section
const publishedAnnouncementsContainerStyle = {
  marginTop: "2.5rem",
  paddingTop: "1.5rem",
  borderTop: "2px solid #e2e8f0",
};

const publishedAnnouncementsHeaderStyle = {
  fontSize: "1.25rem",
  fontWeight: "600",
  color: "#334155",
  marginBottom: "1rem",
};

const announcementsListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
};

const announcementCardStyle = {
  backgroundColor: "#f8fafc",
  borderRadius: "8px",
  padding: "1rem",
  border: "1px solid #e2e8f0",
  transition: "all 0.2s",
  ":hover": {
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  },
};

const announcementHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "0.5rem",
};

const announcementTitleStyle = {
  margin: "0",
  fontSize: "1rem",
  fontWeight: "600",
  color: "#1e293b",
};

const announcementDateStyle = {
  fontSize: "0.75rem",
  color: "#64748b",
};

const announcementContentStyle = {
  fontSize: "0.875rem",
  color: "#334155",
  margin: "0",
  lineHeight: "1.5",
};

const viewAllButtonStyle = {
  marginTop: "1.5rem",
  padding: "0.625rem 1rem",
  backgroundColor: "#f1f5f9",
  color: "#334155",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  fontSize: "0.875rem",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.2s",
  textAlign: "center",
  width: "100%",
  ":hover": {
    backgroundColor: "#e2e8f0",
  },
};

export default AddAnnouncementsPage;